/**
 * Library item discriminated union
 *
 * The /mylibrary2 page collapses three different backend response shapes
 * into one rendered list: capsules (getAllMyCapsulesWithPaging),
 * SequencerProjects (listWorkspaceProjects / getSequencerProjects), and
 * shared-with-me entries (getProjectsSharedWithMe). Each shape has a
 * different rule for which id to pass to the editor:
 *
 *   - capsule        -> capsule.projectId (nullable for legacy capsules)
 *   - sequencerProject -> sequencerProject._id
 *   - sharedProject  -> sharedProject.project._id
 *
 * Historically the frontend sniffed fields (workspaceId, referenceProject,
 * audioPartsInfo, ...) to tell the shapes apart. That heuristic was brittle
 * and silently regressed whenever the backend added a field — most recently
 * when capsule responses started including `workspaceId: null` in personal
 * space. See commits c7ece30a0 and 3f42aaede for two earlier attempts at
 * tightening the heuristic.
 *
 * The fix is to have the backend stamp an explicit discriminator
 * (`itemType`) on every list item and have the frontend dispatch on that
 * discriminator. `inferItemTypeForLegacyResponse` below is a one-shot
 * safety net for old backends during rollout; new frontend code should
 * never need to fall back to it.
 */

export const LIBRARY_ITEM_TYPES = {
    CAPSULE: 'capsule',
    SEQUENCER_PROJECT: 'sequencerProject',
    SHARED_PROJECT: 'sharedProject',
} as const;

export type LibraryItemType = typeof LIBRARY_ITEM_TYPES[keyof typeof LIBRARY_ITEM_TYPES];

/**
 * Action keys exposed by the ProjectCard's overflow menu.
 *
 * `transcribe` is reserved for legacy capsules with no linked project — it
 * routes the user into the Transcribe & Edit flow so a project gets created
 * on demand. The rest match the master capsule context-menu options.
 */
export const PROJECT_CARD_MENU_ACTIONS = [
    'edit',
    'duplicate',
    'transcribe',
    'download',
    'share',
    'comments',
    'move',
    'delete',
] as const;

export type ProjectCardMenuAction = typeof PROJECT_CARD_MENU_ACTIONS[number];

/** Common fields shared by all three variants. */
interface LibraryItemBase {
    _id?: string;
    id?: string;
    name?: string;
    title?: string;
    createdDate?: string | number;
    creationDate?: string | number;
    lastUpdatedDate?: string | number;
    grantedDate?: string | number;
    folderId?: string | null;
    processedVideo?: Record<string, unknown> | string | null;
    processedHQAudio?: Record<string, unknown> | string | null;
    processedAudio?: Record<string, unknown> | string | null;
    auphonicProcessedAudio?: Record<string, unknown> | null;
    durationInSeconds?: number;
    creatorInfoJson?: Record<string, unknown>;
    ownerUserUID?: string;
}

export interface CapsuleLibraryItem extends LibraryItemBase {
    itemType: typeof LIBRARY_ITEM_TYPES.CAPSULE;
    /** Linked editable project id. Null for legacy pre-project capsules. */
    projectId?: string | null;
    capsuleId?: string;
    capsuleUID?: string;
    capsuleRefUID?: string;
    workspaceId?: string | null;
    audioPartsInfo?: Array<{
        uri: string;
        locationKey?: string;
        locationEtag?: string;
        duration?: number;
        size?: number;
        lastUdpdatedDate?: string;
    }>;
    consumerUserUIDs?: unknown;
    repliesCapsuleRefUID?: unknown;
    imageArtUrl?: string;
}

export interface SequencerProjectLibraryItem extends LibraryItemBase {
    itemType: typeof LIBRARY_ITEM_TYPES.SEQUENCER_PROJECT;
    /** Root/anchor project id. Same as _id for the root doc. */
    referenceProjectUID?: string;
    referenceProject?: boolean;
    serializedDataFileLocation?: string;
    workspaceId?: string | null;
    visibility?: string;
    version?: number;
    authorUID?: string;
}

export interface SharedProjectOwner {
    userUID?: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    profileImageUrl?: string;
}

export interface SharedProjectLibraryItem extends LibraryItemBase {
    itemType: typeof LIBRARY_ITEM_TYPES.SHARED_PROJECT;
    /** The shared SequencerProject the guest was granted access to. */
    project: {
        _id?: string;
        projectId?: string;
        name?: string;
        createdDate?: string | number;
        lastUpdatedDate?: string | number;
        ownerUserUID?: string;
    } | null;
    projectId?: string;
    accessLevel?: string;
    guestRecordId?: string;
    owner?: SharedProjectOwner | null;
    ownerUsername?: string;
}

export type LibraryItem =
    | CapsuleLibraryItem
    | SequencerProjectLibraryItem
    | SharedProjectLibraryItem;

/**
 * Legacy fallback: infer an itemType for responses that predate the
 * backend sending the discriminator. Only called once, at the API
 * response wrapper layer. New code in LibraryPage should always read
 * item.itemType directly.
 *
 * Rules (narrow by design — when in doubt, treat as capsule, which is
 * what the personal-library endpoint returns):
 *
 *   - A record with a non-null `project` sub-object is a sharedProject
 *     (that nested object is uniquely produced by getProjectsSharedWithMe).
 *   - A record with `referenceProject` or `serializedDataFileLocation`
 *     is a sequencerProject (both fields are unique to the
 *     SequencerProject schema).
 *   - Everything else defaults to capsule. This matches the pre-fix
 *     behavior of the frontend's getLibraryItemProjectId, which used
 *     the capsule rule as its default branch.
 */
export function inferItemTypeForLegacyResponse(
    item: Record<string, unknown>,
    listSource: LibraryItemType,
): LibraryItemType {
    if (!item || typeof item !== 'object') {
        return listSource;
    }

    // Capsule-unique fields are the strongest positive signal — both
    // the backend capsule DTO and older mock fixtures always carry at
    // least one of these. Check this first to avoid mis-classifying a
    // capsule fixture that happens to carry a `project` convenience
    // sub-object as a sharedProject.
    if (
        item.audioPartsInfo !== undefined ||
        item.consumerUserUIDs !== undefined ||
        item.capsuleId !== undefined ||
        item.capsuleRefUID !== undefined
    ) {
        return LIBRARY_ITEM_TYPES.CAPSULE;
    }

    // SequencerProject-only fields. Capsules never carry these.
    if (
        item.referenceProject !== undefined ||
        item.serializedDataFileLocation !== undefined ||
        item.referenceProjectUID !== undefined ||
        item.version !== undefined
    ) {
        return LIBRARY_ITEM_TYPES.SEQUENCER_PROJECT;
    }

    // Shared-with-me entries always carry a nested `project` object
    // built by getProjectsSharedWithMe. Restrict this check to the
    // shared-with-me list source so a capsule fixture that happens to
    // carry a `project` convenience field doesn't get routed here.
    if (listSource === LIBRARY_ITEM_TYPES.SHARED_PROJECT) {
        const sharedProject = item.project;
        if (
            sharedProject &&
            typeof sharedProject === 'object' &&
            !Array.isArray(sharedProject)
        ) {
            return LIBRARY_ITEM_TYPES.SHARED_PROJECT;
        }
    }

    // Default to the list source. Personal lists produce capsules,
    // workspace lists produce sequencer projects, shared-with-me lists
    // produce shared-project entries.
    return listSource;
}

/**
 * Stamp itemType on a raw list response.
 *
 * `listSource` tells the helper which backend endpoint produced the
 * response — used as the default when the record has no itemType AND
 * the legacy inference rule can't narrow further.
 */
export function stampItemType(
    rawItems: Array<Record<string, unknown>>,
    listSource: LibraryItemType,
): LibraryItem[] {
    if (!Array.isArray(rawItems)) {
        return [];
    }
    return rawItems.map((rawItem) => {
        const existing = rawItem && typeof rawItem === 'object' ? rawItem.itemType : undefined;
        if (existing === LIBRARY_ITEM_TYPES.CAPSULE ||
            existing === LIBRARY_ITEM_TYPES.SEQUENCER_PROJECT ||
            existing === LIBRARY_ITEM_TYPES.SHARED_PROJECT) {
            return rawItem as unknown as LibraryItem;
        }
        const inferred = inferItemTypeForLegacyResponse(rawItem, listSource);
        return Object.assign({}, rawItem, { itemType: inferred }) as unknown as LibraryItem;
    });
}

/**
 * Return the openable project id for the item, or null if the item has
 * no editable project (legacy pre-project capsule). Callers should show
 * a user-facing "no editable project" message when this returns null.
 */
export function getOpenableProjectId(item: LibraryItem): string | null {
    switch (item.itemType) {
        case LIBRARY_ITEM_TYPES.CAPSULE: {
            if (item.projectId && item.projectId.length > 0) {
                return item.projectId;
            }
            const nestedProject = (item as unknown as { project?: { _id?: string; projectId?: string } }).project;
            const nestedId = nestedProject && (nestedProject._id || nestedProject.projectId);
            return nestedId ? String(nestedId) : null;
        }
        case LIBRARY_ITEM_TYPES.SEQUENCER_PROJECT: {
            const id = item._id || item.id;
            return id ? String(id) : null;
        }
        case LIBRARY_ITEM_TYPES.SHARED_PROJECT: {
            const sharedProject = item.project;
            if (!sharedProject) {
                return null;
            }
            const sharedId = sharedProject._id || sharedProject.projectId;
            return sharedId ? String(sharedId) : null;
        }
        default:
            return null;
    }
}

/** Returns the capsule id if the item has one, or null otherwise. */
export function getCapsuleIdForItem(item: LibraryItem): string | null {
    if (item.itemType !== LIBRARY_ITEM_TYPES.CAPSULE) {
        return null;
    }
    const explicit = item.capsuleId || item.capsuleUID || item.capsuleRefUID;
    if (explicit) {
        return String(explicit);
    }
    const capsuleOwnId = item._id || item.id;
    return capsuleOwnId ? String(capsuleOwnId) : null;
}

/** Returns the id the folder-assignment APIs expect for the given item. */
export function getFolderAssignmentId(
    item: LibraryItem,
    isWorkspaceScoped: boolean,
): string | null {
    if (isWorkspaceScoped) {
        return getOpenableProjectId(item) || getCapsuleIdForItem(item);
    }
    return getCapsuleIdForItem(item) || getOpenableProjectId(item);
}

/** Best-effort human title for sort/filter. */
export function getLibraryItemDisplayTitle(item: LibraryItem): string {
    if (item.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT) {
        const sharedProject = item.project;
        return (sharedProject && sharedProject.name) || item.title || item.name || '';
    }
    const nestedProject = (item as unknown as { project?: { name?: string } }).project;
    return item.title || item.name || (nestedProject && nestedProject.name) || '';
}

/** Best-effort created timestamp (ms since epoch), 0 on failure. */
export function getLibraryItemDisplayTimestamp(item: LibraryItem): number {
    const sharedProject = item.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT
        ? item.project
        : null;
    const rawDate =
        item.createdDate ||
        item.creationDate ||
        item.lastUpdatedDate ||
        (item.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT ? item.grantedDate : undefined) ||
        (sharedProject ? sharedProject.createdDate : undefined) ||
        (sharedProject ? sharedProject.lastUpdatedDate : undefined) ||
        0;
    const timestamp = new Date(rawDate as string | number).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
}

/** Type guards for callers that need narrowing. */
export function isCapsule(item: LibraryItem): item is CapsuleLibraryItem {
    return item.itemType === LIBRARY_ITEM_TYPES.CAPSULE;
}
export function isSequencerProject(item: LibraryItem): item is SequencerProjectLibraryItem {
    return item.itemType === LIBRARY_ITEM_TYPES.SEQUENCER_PROJECT;
}
export function isSharedProject(item: LibraryItem): item is SharedProjectLibraryItem {
    return item.itemType === LIBRARY_ITEM_TYPES.SHARED_PROJECT;
}
